import { Controller } from '@hotwired/stimulus'

// Lets mouse users drag an overflow container to scroll it: a mouse offers no
// native way to scroll horizontally (the wheel is vertical and the scrollbar
// is hidden). Touch pointers are ignored and keep native scrolling.
export default class extends Controller {
  static classes = ['grab', 'grabbing']

  // Movement below the threshold stays a click, so imprecise clicks don't
  // register as drags
  static values = { threshold: { type: Number, default: 5 } }

  connect() {
    // Only advertise the grab affordance when there is something to drag
    this.observer = new ResizeObserver(() => this.updateGrabCursor())
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer.disconnect()
    this.element.classList.remove(...this.grabbingClasses)
  }

  press(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return
    if (this.element.scrollWidth <= this.element.clientWidth) return

    this.startX = event.clientX
    this.startScrollLeft = this.element.scrollLeft
    this.pressed = true
    this.dragged = false
  }

  drag(event) {
    if (!this.pressed) return

    const delta = event.clientX - this.startX
    if (!this.dragged) {
      if (Math.abs(delta) < this.thresholdValue) return
      // Capturing the pointer keeps the drag alive once it leaves the
      // container, and pins hover — and with it the grabbing cursor — to it
      this.dragged = true
      this.element.setPointerCapture(event.pointerId)
      this.element.classList.remove(...this.grabClasses)
      this.element.classList.add(...this.grabbingClasses)
    }

    this.element.scrollLeft = this.startScrollLeft - delta
  }

  release() {
    this.pressed = false
    this.element.classList.remove(...this.grabbingClasses)
    this.updateGrabCursor()
  }

  // A click still fires on the link under the pointer when a drag is released
  // over one; swallow it so the drag doesn't navigate
  blockClick(event) {
    if (!this.dragged) return
    event.preventDefault()
    event.stopPropagation()
    this.dragged = false
  }

  // Suppress the links' native drag-and-drop, which would hijack the pointer
  // mid-drag
  blockNativeDrag(event) {
    if (this.pressed) event.preventDefault()
  }

  // Also an action: a Turbo morph strips the JS-added grab class, so it's
  // reapplied on turbo:morph-element
  updateGrabCursor() {
    if (this.pressed) return

    const overflowing = this.element.scrollWidth > this.element.clientWidth
    this.element.classList[overflowing ? 'add' : 'remove'](...this.grabClasses)
  }
}
